<template>
  <v-container class="pa-0">
    <v-toolbar dark :color="appColor_Medium">
      <v-toolbar-title>
        <span><v-icon class="mr-2">person</v-icon>Your Profile</span>
      </v-toolbar-title>
    </v-toolbar>
    <v-card v-if="profile">
      <v-card-text>
        <v-container grid-list-md class="pt-0">
          <v-form>
            
            <!-- Email/Avatar -->
            <v-row no-gutters>

              <!-- Avatar -->
              <v-col
                cols="12"
                sm="3"
                class="text-left"
                >
                <v-avatar
                  class="profile"
                  :size="60"
                  color="grey"
                  outlined
                  >
                  <v-img :src="profile.picture" />
                </v-avatar>
              </v-col>

              <!-- Email -->
              <v-col
                cols="12"
                sm="9"
                >
                <v-row>
                  <v-col
                    cols="12"
                    xs="12"
                    class="text-left"
                    >
                    <v-text-field
                      v-model="profile.email"
                      label="Email"
                      disabled
                      hide-details
                    />
                  </v-col>
                </v-row>
              </v-col>
            </v-row>

            <!-- Name/Nickname -->
            <v-row>

              <!-- Name -->
              <v-col
                cols="12"
                sm="8"
                >
                <v-text-field
                  v-model="profile.name"
                  v-validate="'required|max:100'"
                  label="Full Name"
                  :error-messages="errors.collect('name')"
                  data-vv-name="name"
                  hide-details
                />
              </v-col>

              <!-- Nickname -->
              <v-col
                cols="12"
                sm="4"
                >
                <v-text-field
                  v-model="profile.nickname"
                  v-validate="'required|max:50'"
                  label="Nickname"
                  :error-messages="errors.collect('nickname')"
                  data-vv-name="nickname"
                  hide-details
                />
              </v-col>
            </v-row>

            <!-- Company -->
            <v-row>
              <v-col
                cols="12"
                sm="12"
                >
                <v-text-field
                  v-model="profile.companyName"
                  v-validate="'max:100'"
                  label="Company Name"
                  :error-messages="errors.collect('companyName')"
                  data-vv-name="companyName"
                  hide-details
                />
              </v-col>
            </v-row>

            <!-- Phones -->
            <v-row>

              <!-- Phone 1 -->
              <v-col
                cols="12"
                sm="6"
                >
                <v-text-field
                  v-model="profile.phone1"
                  label="Phone 1"
                  prepend-icon="phone"
                  clearable
                  hide-details
                />
              </v-col>

              <!-- Phone 2 -->
              <v-col
                cols="12"
                sm="6"
                >
                <v-text-field
                  v-model="profile.phone2"
                  label="Phone 2"
                  prepend-icon="phone"
                  clearable
                  hide-details
                />
              </v-col>
            </v-row>

            <!-- BB/HX Customer -->
            <v-row>

              <!-- BB Customer -->
              <v-col
                cols="12"
                sm="6"
                >
                <v-checkbox
                  v-if="!profile.isBeefboosterCustomer || !profile.isBeefboosterValid"
                  v-model="profile.isBeefboosterCustomer"
                  label="Beefbooster Customer"
                  :color="appColor_Selected"
                  hide-details
                  class="pa-0"
                />
                <v-card-text v-else class="px-0 pt-4 pb-0">
                  <span class="text-left text-no-wrap">
                    <v-badge inline icon="done" color="success"></v-badge>
                    Beefbooster Approved
                  </span>
                </v-card-text>
              </v-col>

              <!-- Hx Customer -->
              <v-col
                cols="12"
                sm="6"
                >
                <v-checkbox
                  v-if="!profile.isHerdtraxCustomer || !profile.isHerdtraxValid"
                  v-model="profile.isHerdtraxCustomer"
                  label="Herdtrax Customer"
                  :color="appColor_Selected"
                  hide-details
                  class="pa-0"
                />
                <v-card-text v-else class="px-0 pt-4 pb-0">
                  <span class="text-left text-no-wrap">
                    <v-badge inline icon="done" color="success"></v-badge>
                    Herdtrax Approved
                  </span>
                </v-card-text>
              </v-col>
            </v-row>

            <!-- Herd Size -->
            <v-row no-gutters>
              <v-col
                cols="12"
                sm="6"
                >
                <number-field
                  v-model="profile.cowHerdSize"
                  :label="'Herd Size'"
                  :min="1"
                  :max="99999"
                  :clearable="true"
                  class="pt-3"
                />
              </v-col>
            </v-row>

            <!-- Bull Preferences -->
            <template v-if="showBullPreferences">
              <v-row no-gutters>
                <v-col
                  cols="12"
                  sm="12"
                  >
                  <v-textarea
                    v-model="profile.bullPreferences"
                    v-validate="'max:1000'"
                    label="Bull Preferences"
                    :error-messages="errors.collect('bullPreferences')"
                    data-vv-name="bullPreferences"
                    hide-details="auto"
                    clearable
                    dense
                    class="mt-6"
                  />
                </v-col>
              </v-row>
            </template>
          </v-form>
        </v-container>
      </v-card-text>

      <v-divider />

      <v-card-actions class="py-3">
        <v-spacer />
        <v-btn
          text
          :disabled="isSaving"
          @click="cancel"
          >
          Cancel
        </v-btn>        
        <v-btn
          :color="appColor_ButtonOK"
          text
          :loading="isSaving"
          :disabled="!valid || isSaving"
          @click="save"
          >
          Save
          </v-btn>
      </v-card-actions>
    </v-card>

    <!-- Error Dialog -->
    <v-dialog
      v-model="errorDialog"
      max-width="400px"
      >
      <app-error
        :error-title="errorTitle"
        :error-text="errorText"
        :error-severity="errorSeverity"
        @close="errorDialog = false"
      />
    </v-dialog>
  </v-container>
</template>

<script>
import appService from '@/app.service'
import AppError from '@/components/AppError'
import NumberField from '@/components/NumberField'
import { AppColor } from '@/config.js'

export default {
  name: 'ProfileEdit',
  components: {
    AppError,
    NumberField
  },
  $_veeValidate: {
    validator: 'new'
  },
  props: {
    profile: {
      type: Object,
      default: null
    }
  },
  data () {
    return {
      appColor_Light: AppColor.Light,
      appColor_Medium: AppColor.Medium,
      appColor_Progress: AppColor.Progress,
      appColor_Selected: AppColor.Selected,
      appColor_ButtonOK: AppColor.ButtonOK,

      isSaving: false,
      profileModel: {},
      dictionary: {
        custom: {
          name: {
            required: () => 'Name is required',
            max: 'Name cannot be more 100 characters'
          },
          nickname: {
            required: () => 'Nickname is required',
            max: 'Nickname cannot be more than 50 characters'
          },
          companyName: {
            max: 'Company Name cannot be more 100 characters'
          },
          bullPreferences: {
            max: 'Bull Preferences cannot be more 1000 characters'
          }
        }
      },
      
      // error handling
      errorDialog: false,
      errorTitle: 'Internal Error',
      errorText: null,
      errorSeverity: null
    }
  },
  computed: {
    showBullPreferences () {
      return this.profile.isBeefboosterCustomer || this.profile.isAdminy
    },
    valid () {
      return this.profile.name && this.profile.nickname
    }
  },
  mounted () {
    this.$validator.localize('en', this.dictionary)
    this.$validator.reset()
  },
  methods: {
    save () {
      const self = this
      self.$validator.validateAll().then(valid => {
        if (valid) {
          self.isSaving = true

          // unfortunately numbers get converted to string - convert back to number
          if (self.profile.cowHerdSize !== null) {
            self.profile.cowHerdSize = parseInt(self.profile.cowHerdSize)
          }

          self.profile.name = self.trimText(self.profile.name)
          self.profile.nickname = self.trimText(self.profile.nickname)
          self.profile.companyName = self.trimText(self.profile.companyName)
          self.profile.phone1 = self.trimText(self.profile.phone1)
          self.profile.phone2 = self.trimText(self.profile.phone2)
          self.profile.bullPreferences = self.trimText(self.profile.bullPreferences)

          appService.updateUserProfile(self.profile.id, self.profile).then(data => {
            if (data.errorSeverity > 0) {
              self.$emit('error', data.errorMessage)
            } else {
              self.$emit('save')
            }
            self.isSaving = false
          })
        }
      }).catch((error) => {
        self.errorText = error.errorMessage
        self.errorDialog = true
      })
    },

    trimText(text) {
      return !this.isNullOrUndefined(text)
        ? text.length > 0 ? text.trim() : null
        : null
    },
    
    cancel () {
      this.$emit('cancel')
    },

    isNullOrUndefined (obj) {
      return obj === undefined 
        || typeof obj === 'undefined' 
        || obj === null
    }
  }
}
</script>
